import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import StatusBadge from "../components/StatusBadge";
import { toast } from "sonner";
import { X, PhoneCall, CheckCircle2, IndianRupee, Search } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { formatDate, relTime } from "../lib/date";

const STATUS_OPTIONS = [
  { v: "", label: "All statuses" },
  { v: "new", label: "New" },
  { v: "not_connected", label: "Not Connected" },
  { v: "completed", label: "Completed" },
  { v: "hold", label: "Hold" },
  { v: "rejected", label: "Rejected" },
  { v: "follow_up", label: "Follow-up" },
];

export default function TelecallerDashboard() {
  const { user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [leads, setLeads] = useState([]);
  const status = searchParams.get("status") || "";
  const setStatus = (v) => { const p = new URLSearchParams(searchParams); v ? p.set("status", v) : p.delete("status"); setSearchParams(p); };
  const [detail, setDetail] = useState(null);
  const [summary, setSummary] = useState(null);
  const [search, setSearch] = useState("");

  const load = () => {
    const params = {};
    if (status) params.status = status;
    if (search.trim()) params.search = search.trim();
    api.get("/leads", { params }).then((r) => setLeads(r.data));
    api.get("/reports/summary").then((r) => setSummary(r.data));
  };
  // Debounced search
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [status, user, search]);

  return (
    <div className="p-4 sm:p-8">
      <div className="mb-6">
        <div className="mb-1 text-[11px] uppercase tracking-widest text-zinc-500">Your queue</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-black">My Leads</h1>
      </div>

      {summary && (
        <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
          <MiniStat label="Assigned to me" value={summary.total_leads} icon={PhoneCall}/>
          <MiniStat label="Completed" value={summary.completed_count} icon={CheckCircle2} accent="text-emerald-600"/>
          <MiniStat label="Sales" value={`₹${summary.total_sales.toLocaleString("en-IN")}`} icon={IndianRupee} accent="text-emerald-600"/>
          <MiniStat label="Conversion" value={`${summary.conversion_rate}%`}/>
        </div>
      )}

      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="pointer-events-none absolute left-2.5 top-2.5 h-3.5 w-3.5 text-zinc-400"/>
          <input data-testid="tc-search-input" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search lead by phone or name (e.g. 9876543210)"
            className="w-full rounded-sm border border-zinc-300 bg-white pl-8 pr-3 py-1.5 text-sm outline-none focus:border-black" />
        </div>
        <select data-testid="tc-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
          className="rounded-sm border border-zinc-300 bg-white px-2 py-1.5 text-xs outline-none focus:border-black">
          {STATUS_OPTIONS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
        </select>
        <div className="text-xs text-zinc-500">{leads.length} results</div>
      </div>

      {/* Mobile card list */}
      <div className="sm:hidden space-y-2">
        {leads.length === 0 && <div className="rounded-sm border border-zinc-200 bg-white p-6 text-center text-sm text-zinc-500">No leads found.</div>}
        {leads.map(l => (
          <button key={l.id} data-testid={`tc-update-${l.id}`} onClick={() => setDetail(l)}
            className="w-full text-left rounded-sm border border-zinc-200 bg-white p-3 hover:border-zinc-400">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-black truncate">{l.name || "—"}</div>
                <div className="text-xs text-zinc-600">{l.phone}</div>
                {l.campaign && <div className="text-[11px] text-zinc-500 truncate">{l.campaign}</div>}
              </div>
              <StatusBadge status={l.status}/>
            </div>
            {l.sale_amount ? <div className="mt-1 text-xs font-medium text-emerald-700">₹{l.sale_amount.toLocaleString("en-IN")}</div> : null}
            <div className="mt-1 text-[10px] text-zinc-400">Updated {relTime(l.updated_at)}</div>
          </button>
        ))}
      </div>

      {/* Desktop table */}
      <div className="hidden sm:block overflow-x-auto rounded-sm border border-zinc-200 bg-white">
        <table className="w-full text-sm min-w-[720px]" data-testid="tc-leads-table">
          <thead className="bg-zinc-50 text-left text-[11px] uppercase tracking-wider text-zinc-500">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Phone</th>
              <th className="px-4 py-3">Campaign</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Sale</th>
              <th className="px-4 py-3">Received</th>
              <th className="px-4 py-3">Last update</th>
              <th className="px-4 py-3">Action</th>
            </tr>
          </thead>
          <tbody>
            {leads.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-sm text-zinc-500">No leads assigned yet.</td></tr>}
            {leads.map(l => (
              <tr key={l.id} className="border-t border-zinc-200 hover:bg-zinc-50">
                <td className="px-4 py-2.5 font-medium text-black">{l.name || "—"}</td>
                <td className="px-4 py-2.5 tabular-nums">
                  <a data-testid={`tc-call-${l.id}`} href={`tel:${l.phone}`} className="text-blue-700 hover:underline">{l.phone}</a>
                  {l.email && <div className="text-[11px] text-zinc-500">{l.email}</div>}
                </td>
                <td className="px-4 py-2.5 text-zinc-700">{l.campaign || l.ad_name || "—"}</td>
                <td className="px-4 py-2.5"><StatusBadge status={l.status}/></td>
                <td className="px-4 py-2.5 tabular-nums">{l.sale_amount ? `₹${l.sale_amount.toLocaleString("en-IN")}` : "—"}</td>
                <td className="px-4 py-2.5 text-[11px] text-zinc-500" title={formatDate(l.assigned_at || l.created_at)}>{relTime(l.assigned_at || l.created_at)}</td>
                <td className="px-4 py-2.5 text-[11px] text-zinc-500" title={formatDate(l.updated_at)}>{relTime(l.updated_at)}</td>
                <td className="px-4 py-2.5">
                  <button data-testid={`tc-update-${l.id}`} onClick={() => setDetail(l)}
                    className="rounded-sm border border-zinc-300 bg-white px-2.5 py-1 text-xs font-medium hover:bg-zinc-50">
                    Update
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {detail && <UpdateDialog lead={detail} onClose={() => setDetail(null)} onSaved={() => { setDetail(null); load(); }} />}
    </div>
  );
}

function MiniStat({ label, value, icon: Icon, accent }) {
  return (
    <div data-testid={`tc-metric-${label.toLowerCase().replace(/\s+/g, "-")}`} className="rounded-sm border border-zinc-200 bg-white p-4">
      <div className="flex items-start justify-between">
        <div className="text-[11px] uppercase tracking-widest text-zinc-500">{label}</div>
        {Icon && <Icon className={`h-4 w-4 ${accent || "text-zinc-400"}`}/>}
      </div>
      <div className="mt-2 font-heading text-2xl font-bold tracking-tight text-black">{value}</div>
    </div>
  );
}

function UpdateDialog({ lead, onClose, onSaved }) {
  const [status, setStatus] = useState(lead.status);
  const [notes, setNotes] = useState(lead.notes || "");
  const [sale, setSale] = useState(lead.sale_amount || 0);

  const save = async () => {
    try {
      await api.patch(`/leads/${lead.id}`, { status, notes, sale_amount: parseFloat(sale) || 0 });
      toast.success("Updated"); onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-sm border border-zinc-200 bg-white p-6">
        <div className="mb-4 flex items-start justify-between">
          <div>
            <div className="text-[11px] uppercase tracking-widest text-zinc-500">Update lead</div>
            <h3 className="font-heading text-xl font-bold text-black">{lead.name}</h3>
            <div className="text-sm text-zinc-500">{lead.phone}</div>
            <div className="mt-1 text-[11px] text-zinc-400">
              Received {formatDate(lead.assigned_at || lead.created_at)} · Updated {formatDate(lead.updated_at)}
            </div>
          </div>
          <button onClick={onClose} className="rounded-sm p-1 hover:bg-zinc-100"><X className="h-4 w-4"/></button>
        </div>

        {lead.message && <div className="mb-4 rounded-sm border border-zinc-200 bg-zinc-50 p-3 text-xs text-zinc-700"><span className="mb-1 block text-[10px] uppercase tracking-wider text-zinc-500">WhatsApp message</span>{lead.message}</div>}

        <div className="mb-3">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Status</label>
          <select data-testid="tc-detail-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black">
            <option value="new">New</option>
            <option value="not_connected">Not Connected</option>
            <option value="completed">Completed</option>
            <option value="hold">Hold</option>
            <option value="rejected">Rejected</option>
            <option value="follow_up">Follow-up</option>
          </select>
        </div>

        {status === "completed" && (
          <div className="mb-3">
            <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Sale Amount (₹)</label>
            <input data-testid="tc-detail-sale" type="number" value={sale} onChange={(e) => setSale(e.target.value)}
              className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
          </div>
        )}

        <div className="mb-4">
          <label className="mb-1 block text-[11px] uppercase tracking-wider text-zinc-500">Notes</label>
          <textarea data-testid="tc-detail-notes" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)}
            className="w-full rounded-sm border border-zinc-300 px-3 py-2 text-sm outline-none focus:border-black" />
        </div>

        <button data-testid="tc-detail-save" onClick={save} className="w-full rounded-sm bg-black px-4 py-2.5 text-sm font-semibold text-white hover:bg-zinc-800">
          Save
        </button>
      </div>
    </div>
  );
}
